import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useBrand } from "../lib/brand";
import { LANGUAGES } from "../lib/languages";
import { BUSINESS_STAGES } from "../lib/business-stages";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { ArrowRight, ArrowLeft, Loader2, Lock, Clock, ShieldCheck, Sparkles, Target, LineChart } from "lucide-react";
import { toast } from "sonner";

export default function Assessment() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { tenant } = useBrand();
  const slug = params.slug || tenant?.slug || "";
  const nav = useNavigate();

  const [consultant, setConsultant] = useState(null);
  const [catalog, setCatalog] = useState({ countries: [], industries: [], functions: [] });
  // phases: landing → setup → generating → questions
  const [phase, setPhase] = useState("landing");
  const [sel, setSel] = useState({
    country: "",
    industry: "",
    function: "",
    organisation_size: "",
    language: "",
  });
  const [locked, setLocked] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Pack 8 · taxonomy cascade. `taxCountries` loads once; industries and
  // functions re-load whenever the upstream selection changes so the picker
  // is ranked by (country×industry, industry×function) cascade weight
  // instead of a hardcoded alphabetical list. Graceful degradation: if any
  // /api/taxonomy call fails we fall back to the legacy `/api/catalog`
  // string arrays (P9 in the architecture doc).
  const [taxCountries, setTaxCountries] = useState(null);
  const [taxIndustries, setTaxIndustries] = useState(null);
  const [taxFunctions, setTaxFunctions] = useState(null);

  // Pick the value we send to the backend for a taxonomy node.
  // Historically the DB stores human-readable strings (e.g. "Retail & E-commerce")
  // on campaigns/leads/templates. New taxonomy names are slightly longer
  // ("Retail Trade & E-commerce") — we intake the FIRST ALIAS when present
  // so all existing joins and cached templates keep matching.
  const intakeValueFor = (node) => (node?.aliases?.[0] || node?.name || "");

  useEffect(() => {
    if (!slug) return;
    api.get(`/consultants/by-slug/${slug}`).then((r) => setConsultant(r.data)).catch(() => toast.error("Consultant not found"));
    api.get("/catalog").then((r) => setCatalog(r.data));
    api.get("/taxonomy/countries")
      .then((r) => setTaxCountries(r.data.countries || []))
      .catch(() => setTaxCountries([]));  // fall back to catalog
  }, [slug]);

  // Load industries whenever the selected country changes. Cascade weight is
  // computed by the backend so we just render in the order it returns.
  useEffect(() => {
    if (!sel.country || !taxCountries?.length) return;
    const country = taxCountries.find((c) => c.name === sel.country || c.aliases?.includes?.(sel.country));
    if (!country) { setTaxIndustries([]); return; }
    api.get(`/taxonomy/industries?country_id=${encodeURIComponent(country.id)}`)
      .then((r) => setTaxIndustries(r.data.industries || []))
      .catch(() => setTaxIndustries([]));
  }, [sel.country, taxCountries]);

  // Load functions whenever the selected industry changes.
  useEffect(() => {
    if (!sel.industry || !taxIndustries?.length) return;
    const industry = taxIndustries.find(
      (i) => intakeValueFor(i) === sel.industry || i.name === sel.industry
    );
    if (!industry) { setTaxFunctions([]); return; }
    api.get(`/taxonomy/functions?industry_id=${encodeURIComponent(industry.id)}`)
      .then((r) => setTaxFunctions(r.data.functions || []))
      .catch(() => setTaxFunctions([]));
  }, [sel.industry, taxIndustries]);

  // Scoped/campaign URL — lock the 3 params and default language to consultant's primary.
  useEffect(() => {
    if (!consultant) return;
    if (locked) return;
    const c = searchParams.get("country");
    const i = searchParams.get("industry");
    const f = searchParams.get("function");
    if (c && i && f) {
      // Allow-list precedence: consultant explicit list → any of catalog/taxonomy
      // strings we currently know about. Scoped URLs pre-date the taxonomy so
      // the values are still legacy string labels.
      const knownCountries = (catalog.countries || []).concat((taxCountries || []).map(x => x.name));
      const knownIndustries = (catalog.industries || []).concat((taxIndustries || []).map(intakeValueFor));
      const knownFunctions = (catalog.functions || []).concat((taxFunctions || []).map(x => x.name));
      const allowedC = consultant.countries?.length ? consultant.countries : knownCountries;
      const allowedI = consultant.industries?.length ? consultant.industries : knownIndustries;
      const allowedF = consultant.functions?.length ? consultant.functions : knownFunctions;
      const valid = (!allowedC.length || allowedC.includes(c))
                 && (!allowedI.length || allowedI.includes(i))
                 && (!allowedF.length || allowedF.includes(f));
      if (!valid) return;
      setSel((s) => ({ ...s, country: c, industry: i, function: f, language: s.language || consultant.primary_language || "en" }));
      setLocked(true);
    }
  }, [consultant, catalog, taxCountries, taxIndustries, taxFunctions, searchParams, locked]);

  const start = async () => {
    if (!locked && (!sel.country || !sel.industry || !sel.function)) {
      return toast.error("Please choose country, industry and function");
    }
    if (!sel.organisation_size) return toast.error("Please choose your business stage");
    if (!sel.language) return toast.error("Please choose a language");

    setPhase("generating");
    try {
      const { data } = await api.post("/assessment/start", {
        consultant_slug: slug,
        country: sel.country,
        industry: sel.industry,
        function: sel.function,
        organisation_size: sel.organisation_size,
        language: sel.language,
      });
      setAssessment(data);
      setPhase("questions");
    } catch (e) {
      toast.error("Failed to generate Business Growth Discovery™");
      setPhase("setup");
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        assessment_id: assessment.assessment_id,
        answers: assessment.template.questions.map((q) => ({ question_id: q.id, answer: answers[q.id] })),
      };
      const { data } = await api.post("/assessment/submit", payload);
      sessionStorage.setItem(`bp_results_${data.assessment_id}`, JSON.stringify({
        ...data,
        country: sel.country,
        industry: sel.industry,
        function: sel.function,
        consultant: assessment.consultant,
      }));
      nav(`/results/${data.assessment_id}`);
    } catch (e) {
      toast.error("Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Derived dropdown options.
  // - Consultant allow-list controls WHICH items appear.
  // - Taxonomy cascade controls THE ORDER when we have weight data for them.
  // - If both fail (offline / brand new install) fall back to legacy /api/catalog.
  const countries = useMemo(() => {
    // Base allow-list: consultant explicit → taxonomy → catalog
    const allow = consultant?.countries?.length
      ? consultant.countries
      : (taxCountries?.length ? taxCountries.map((c) => c.name) : (catalog.countries || []));
    // Order: taxonomy sort order first (currently alphabetical from /countries),
    // then any consultant items the taxonomy doesn't know about.
    const taxOrder = (taxCountries || []).map((c) => c.name);
    const allowSet = new Set(allow);
    const ordered = taxOrder.filter((n) => allowSet.has(n))
      .concat(allow.filter((n) => !taxOrder.includes(n)));
    return ordered.map((name) => ({ value: name, label: name }));
  }, [consultant, taxCountries, catalog]);

  const industries = useMemo(() => {
    const allow = consultant?.industries?.length
      ? consultant.industries
      : (taxIndustries?.length ? taxIndustries.map(intakeValueFor) : (catalog.industries || []));
    // Cascade-weighted order from /taxonomy/industries?country_id=…
    // Each taxonomy row exposes `intakeValueFor(row)` = alias[0] || name;
    // that's the string we want to match against the consultant allow-list
    // (which still uses legacy strings like "Retail & E-commerce").
    const cascade = (taxIndustries || []).map((i) => intakeValueFor(i));
    const allowSet = new Set(allow);
    const ordered = cascade.filter((v) => allowSet.has(v))
      .concat(allow.filter((v) => !cascade.includes(v)));
    // Label follows the taxonomy `name` if we know it, else the raw value.
    const labelFor = (v) => {
      const hit = (taxIndustries || []).find((i) => intakeValueFor(i) === v);
      return hit?.name || v;
    };
    return ordered.map((value) => ({ value, label: labelFor(value) }));
  }, [consultant, taxIndustries, catalog]);

  const functions = useMemo(() => {
    const allow = consultant?.functions?.length
      ? consultant.functions
      : (taxFunctions?.length ? taxFunctions.map((f) => f.name) : (catalog.functions || []));
    const cascade = (taxFunctions || []).map((f) => f.name);
    const allowSet = new Set(allow);
    const ordered = cascade.filter((v) => allowSet.has(v))
      .concat(allow.filter((v) => !cascade.includes(v)));
    return ordered.map((value) => ({ value, label: value }));
  }, [consultant, taxFunctions, catalog]);

  const availableLanguages = useMemo(() => {
    const supported = consultant?.supported_languages?.length
      ? consultant.supported_languages
      : [consultant?.primary_language || "en"];
    return LANGUAGES.filter((l) => supported.includes(l.code));
  }, [consultant]);

  if (!consultant) return <div className="p-10 text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <ConsultantHeader consultant={consultant} />

      {phase === "landing" && (
        <CampaignLanding
          consultant={consultant}
          locked={locked}
          sel={sel}
          onBegin={() => {
            setSel((s) => ({
              ...s,
              language: s.language || consultant.primary_language || "en",
            }));
            setPhase("setup");
          }}
        />
      )}

      {phase === "setup" && (
        <>
          {locked && <ScopedBanner sel={sel} />}
          <Card className="card-elevated p-8" data-testid="assess-intake-card">
            <div className="overline"><span className="accent-line" /> Tell us about your business</div>
            <h1 className="font-display text-3xl mt-3">A few details so we can tailor your discovery</h1>
            <p className="text-muted-foreground mt-2">This helps us generate the right executive questions for you. Takes ~30 seconds.</p>

            <div className="grid gap-5 mt-7">
              {!locked && (
                <>
                  <IntakeField label="Country">
                    <Select onValueChange={(v) => setSel({ ...sel, country: v, industry: "", function: "" })} value={sel.country}>
                      <SelectTrigger data-testid="assess-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent>{countries.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </IntakeField>
                  <IntakeField label="Industry">
                    <Select onValueChange={(v) => setSel({ ...sel, industry: v, function: "" })} value={sel.industry}>
                      <SelectTrigger data-testid="assess-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                      <SelectContent>{industries.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </IntakeField>
                  <IntakeField label="Business function">
                    <Select onValueChange={(v) => setSel({ ...sel, function: v })} value={sel.function}>
                      <SelectTrigger data-testid="assess-function"><SelectValue placeholder="Select function" /></SelectTrigger>
                      <SelectContent>{functions.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </IntakeField>
                </>
              )}

              <IntakeField label="Which best describes your organisation?">
                <Select onValueChange={(v) => setSel({ ...sel, organisation_size: v })} value={sel.organisation_size}>
                  <SelectTrigger data-testid="assess-business-stage"><SelectValue placeholder="Select business stage" /></SelectTrigger>
                  <SelectContent>
                    {BUSINESS_STAGES.map((s) => (
                      <SelectItem key={s.code} value={s.code} data-testid={`stage-opt-${s.code.toLowerCase()}`}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </IntakeField>

              <IntakeField label="Preferred language">
                <Select onValueChange={(v) => setSel({ ...sel, language: v })} value={sel.language}>
                  <SelectTrigger data-testid="assess-language"><SelectValue placeholder="Select language" /></SelectTrigger>
                  <SelectContent>
                    {(availableLanguages.length ? availableLanguages : LANGUAGES.slice(0, 1)).map((l) => (
                      <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </IntakeField>
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <Button
                variant="ghost"
                onClick={() => setPhase("landing")}
                data-testid="assess-back-landing"
                className="rounded-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={start}
                data-testid="assess-start"
                className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90 rounded-full"
              >
                Start Business Growth Discovery™ <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        </>
      )}

      {phase === "generating" && (
        <Card className="card-elevated p-10 text-center">
          <Loader2 className="w-10 h-10 text-accent mx-auto animate-spin" />
          <h2 className="font-display text-2xl mt-6">Generating your Business Growth Discovery™…</h2>
          <p className="text-muted-foreground mt-2">
            Tailoring questions to <span className="text-foreground">{sel.country}</span>, <span className="text-foreground">{sel.industry}</span>, <span className="text-foreground">{sel.function}</span> and your business stage.
          </p>
        </Card>
      )}

      {phase === "questions" && assessment && (
        <QuestionRunner
          template={assessment.template}
          idx={idx}
          setIdx={setIdx}
          answers={answers}
          setAnswers={setAnswers}
          submit={submit}
          submitting={submitting}
        />
      )}
    </div>
  );
}

function ConsultantHeader({ consultant }) {
  return (
    <div className="flex items-center gap-4 mb-10" data-testid="consultant-header">
      {consultant.profile_photo ? (
        <img src={consultant.profile_photo} alt={consultant.name} className="w-12 h-12 rounded-full object-cover border border-border" />
      ) : (
        <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent font-semibold">{consultant.name?.[0]}</div>
      )}
      <div>
        <div className="overline">Business Growth Discovery™ hosted by</div>
        <div className="font-sans-ui font-semibold" data-testid="consultant-name">
          {consultant.name} <span className="text-muted-foreground font-normal">· {consultant.company}</span>
        </div>
      </div>
    </div>
  );
}

function ScopedBanner({ sel }) {
  return (
    <div className="mb-6 flex flex-wrap items-center gap-2 text-xs" data-testid="scoped-locked-banner">
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/10 border border-accent/30 text-accent">
        <Lock className="w-3 h-3" /> Tailored for you
      </span>
      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary/60 border border-border/60 text-foreground" data-testid="scoped-locked-country">{sel.country}</span>
      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary/60 border border-border/60 text-foreground" data-testid="scoped-locked-industry">{sel.industry}</span>
      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary/60 border border-border/60 text-foreground" data-testid="scoped-locked-function">{sel.function}</span>
    </div>
  );
}

function CampaignLanding({ consultant, locked, sel, onBegin }) {
  const headline = consultant.brand_headline
    || "Discover Your Business Gaps. Connect With the Right Expert.";
  const subheadline = consultant.brand_subheadline
    || `A confidential executive diagnostic tailored to your country, industry and business function — hosted by ${consultant.name}.`;

  const benefits = [
    {
      icon: Target,
      title: "Pinpoint your top opportunities",
      body: "Identify the 3 highest-leverage gaps holding back growth — validated against executive benchmarks.",
    },
    {
      icon: LineChart,
      title: "Get a Business Pulse Score™",
      body: "See how your organisation scores across the categories that matter most for your function.",
    },
    {
      icon: Sparkles,
      title: "Unlock a personalised Executive Growth Report™",
      body: "Priority recommendations and a first-90-day action plan — delivered privately, no obligation.",
    },
    {
      icon: ShieldCheck,
      title: "Confidential and executive-grade",
      body: "Designed for CEOs, MDs and Founders. Your answers are shared only with your chosen consultant.",
    },
  ];

  return (
    <>
      {locked && <ScopedBanner sel={sel} />}
      <Card className="card-elevated p-8 md:p-10" data-testid="campaign-landing-card">
        <div className="overline"><span className="accent-line" /> Business Promise™</div>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight" data-testid="landing-headline">
          {headline}
        </h1>
        <p className="text-muted-foreground text-lg mt-4 leading-relaxed max-w-2xl" data-testid="landing-subheadline">
          {subheadline}
        </p>

        <div className="mt-6 flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/30 text-accent" data-testid="landing-time-badge">
            <Clock className="w-3.5 h-3.5" /> 8–10 minutes
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary/60 border border-border/60 text-foreground">
            <ShieldCheck className="w-3.5 h-3.5" /> Confidential
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary/60 border border-border/60 text-foreground">
            <Sparkles className="w-3.5 h-3.5" /> AI-tailored questions
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-9" data-testid="landing-benefits">
          {benefits.map((b) => (
            <div key={b.title} className="flex gap-3 p-4 rounded-lg border border-border/50 bg-secondary/30">
              <div className="w-9 h-9 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0">
                <b.icon className="w-4 h-4 text-accent" />
              </div>
              <div>
                <div className="font-sans-ui font-semibold text-sm">{b.title}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{b.body}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-9 flex flex-wrap items-center gap-4">
          <Button
            onClick={onBegin}
            data-testid="landing-begin"
            className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-base"
          >
            Begin your Business Growth Discovery™ <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <div className="text-xs text-muted-foreground">
            Hosted by <span className="text-foreground font-medium">{consultant.name}</span> · {consultant.company}
          </div>
        </div>
      </Card>
    </>
  );
}

function IntakeField({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function QuestionRunner({ template, idx, setIdx, answers, setAnswers, submit, submitting }) {
  const total = template.questions.length;
  const q = template.questions[idx];
  const cur = answers[q.id];
  const progress = Math.round(((idx) / total) * 100);
  const canNext = cur !== undefined && cur !== null && cur !== "";

  const nextLabel = (() => {
    if (idx !== total - 1) return "Next";
    return submitting ? "Submitting…" : "Submit";
  })();
  const next = () => idx < total - 1 ? setIdx(idx + 1) : submit();
  const prev = () => idx > 0 && setIdx(idx - 1);

  return (
    <Card className="card-elevated p-8">
      <div className="flex items-center justify-between">
        <div className="overline">Question {idx + 1} of {total}</div>
        <div className="text-xs text-muted-foreground">{q.category && q.category.startsWith("_") ? "About your company" : q.category}</div>
      </div>
      <Progress value={progress} className="mt-3 h-1" />

      <h2 className="font-display text-2xl lg:text-3xl mt-7 leading-snug" data-testid={`q-text-${idx}`}>{q.question}</h2>

      <div className="mt-7" data-testid={`q-input-${idx}`}>
        {q.type === "rating" && <RatingInput value={cur} onChange={(v) => setAnswers({ ...answers, [q.id]: v })} />}
        {q.type === "yes_no" && (
          <RadioGroup value={cur} onValueChange={(v) => setAnswers({ ...answers, [q.id]: v })} className="grid grid-cols-2 gap-3">
            {["yes", "no"].map((o) => (
              <label key={o} className={`border rounded-md p-4 cursor-pointer transition-colors ${cur === o ? "border-accent bg-accent/10" : "border-border/60 hover:border-accent/40"}`}>
                <div className="flex items-center gap-3">
                  <RadioGroupItem value={o} id={`${q.id}-${o}`} data-testid={`q-opt-${idx}-${o}`} />
                  <span className="capitalize">{o}</span>
                </div>
              </label>
            ))}
          </RadioGroup>
        )}
        {q.type === "multiple_choice" && (
          <RadioGroup value={cur} onValueChange={(v) => setAnswers({ ...answers, [q.id]: v })} className="grid gap-3">
            {q.options.map((o) => (
              <label key={o} className={`border rounded-md p-4 cursor-pointer transition-colors ${cur === o ? "border-accent bg-accent/10" : "border-border/60 hover:border-accent/40"}`}>
                <div className="flex items-center gap-3">
                  <RadioGroupItem value={o} id={`${q.id}-${o}`} data-testid={`q-opt-${idx}-${o.slice(0,12)}`} />
                  <span>{o}</span>
                </div>
              </label>
            ))}
          </RadioGroup>
        )}
      </div>

      <div className="mt-9 flex items-center justify-between">
        <Button variant="ghost" onClick={prev} disabled={idx === 0} data-testid="q-prev">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button onClick={next} disabled={!canNext || submitting} data-testid="q-next" className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-7">
          {nextLabel}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </Card>
  );
}

function RatingInput({ value, onChange }) {
  return (
    <div className="grid grid-cols-5 gap-3">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} data-testid={`q-rating-${n}`}
          className={`border rounded-md py-5 font-display text-2xl transition-colors ${value === n ? "border-accent bg-accent/10 text-accent" : "border-border/60 hover:border-accent/40"}`}>
          {n}
        </button>
      ))}
      <div className="col-span-5 flex justify-between text-xs text-muted-foreground mt-1">
        <span>Poor</span><span>Excellent</span>
      </div>
    </div>
  );
}
